/**
 * Weight conversion helpers + a UI-display helper that honours the user's
 * `weight_unit_preference` ("lb" | "kg") from the AuthContext.
 *
 * Backend stores weights in whatever unit the caller submitted, alongside a
 * `unit` field. The display layer converts on demand — never overwriting
 * stored values — so a user toggling preference doesn't lose precision.
 */

const G_PER_OZ = 28.3495;
const G_PER_LB = 453.592;
const G_PER_KG = 1000;

/** Convert any input weight to grams. */
export function toGrams(weight, unit) {
  const w = Number(weight);
  if (!Number.isFinite(w)) return 0;
  switch ((unit || "lb").toLowerCase()) {
    case "g":  return w;
    case "kg": return w * G_PER_KG;
    case "oz": return w * G_PER_OZ;
    case "lb":
    default:   return w * G_PER_LB;
  }
}

/** Pretty-format a stored {weight, unit} in the user's preferred unit. */
export function formatWeight(weight, unit, preferredUnit = "lb") {
  const grams = toGrams(weight, unit);
  if (preferredUnit === "kg") {
    // Show grams when under 1 kg (tiny puppies), else kg with 2 decimals.
    if (grams < 1000) return `${Math.round(grams)} g`;
    return `${(grams / G_PER_KG).toFixed(2)} kg`;
  }
  // Imperial: ounces under 1 lb, otherwise lb to 1 decimal.
  if (grams < G_PER_LB) return `${(grams / G_PER_OZ).toFixed(1)} oz`;
  return `${(grams / G_PER_LB).toFixed(1)} lb`;
}

/**
 * Convert a numeric input from one unit to another for form display purposes.
 * Returns a plain Number — caller decides precision.
 */
export function convertWeight(weight, fromUnit, toUnit) {
  if (fromUnit === toUnit) return Number(weight);
  const grams = toGrams(weight, fromUnit);
  switch ((toUnit || "lb").toLowerCase()) {
    case "g":  return grams;
    case "kg": return grams / G_PER_KG;
    case "oz": return grams / G_PER_OZ;
    case "lb":
    default:   return grams / G_PER_LB;
  }
}

/** Returns the "best-fit" entry unit suggestion based on age & preference. */
export function suggestPuppyEntryUnit(preferredUnit = "lb") {
  return preferredUnit === "kg" ? "g" : "oz";  // young puppies are tiny
}
